import { isCommercial, isOutNow, isRunningLate } from './alerts.js'
import { loadOf } from './controller.js'
import { isLiveVisit, totalDriveMinutes } from './travel.js'
import type { BoardFilter, BoardVisit, ColourBy, SchedulablePerson } from './types.js'

/**
 * Pulse figures, chip filters, and how a block tints — all readings of the
 * day already loaded. None of them hit the server.
 */

export const BOARD_FILTERS: Array<{ id: BoardFilter; label: string }> = [
  { id: 'everything', label: 'Everything' },
  { id: 'outNow', label: 'Out now' },
  { id: 'runningLate', label: 'Running late' },
  { id: 'commercial', label: 'Commercial' },
]

export function visitMatchesFilter(visit: BoardVisit, filter: BoardFilter, now = new Date()): boolean {
  switch (filter) {
    case 'everything':
      return true
    case 'outNow':
      return isOutNow(visit)
    case 'runningLate':
      return isRunningLate(visit, now)
    case 'commercial':
      return isCommercial(visit)
    case 'notGoingToPlan':
      return isRunningLate(visit, now) || visit.job.status === 'AWAITING_PARTS'
  }
}

/**
 * How much of a block's meta row fits.
 *
 * `full` adds the duration beside the number; anything below `nameOnly` keeps
 * the meta row and the kind of work; `nameOnly` is the place name alone.
 *
 * The thresholds are lower than the room three lines of text need, because the
 * two boards run out of room in different directions: on the timeline a booking
 * is always a full row tall and duration only buys it width, which `truncate`
 * already handles, and on the day board a booking's height is checked in pixels
 * against `COMPACT_BELOW_PX` before this is consulted. So a half-hour job shows
 * what it has and lets the card clip, rather than being told in advance that it
 * cannot have any of it.
 */
export type BlockMetaLevel = 'full' | 'noDuration' | 'noDot' | 'nameOnly'

export function blockMetaLevel(durationMinutes: number): BlockMetaLevel {
  if (durationMinutes >= 60) return 'full'
  if (durationMinutes >= 35) return 'noDuration'
  if (durationMinutes >= 20) return 'noDot'
  return 'nameOnly'
}

export interface BlockTint {
  /** Fill, plus the ink the card's own lines inherit. */
  className: string
  style?: { backgroundColor: string }
  /** Ink for the kind-of-work line, which is deliberately quieter. */
  muted: string
  /** Ink for the duration beside the number, quieter still. */
  subtle: string
  dot: string
}

/**
 * The status fills are the brand's (`--color-board-*`): the families mean
 * what the app's status badges mean — a quote is warm, a work order is blue,
 * done is green — in the guide's support colours rather than stock ones. A
 * work order, which is most of any day, is Sky under Buster Navy.
 */
const ORDER = { className: 'bg-board-order text-brand-800', dot: '#5377ca' }
const QUOTE = { className: 'bg-board-quote text-board-quote-ink', dot: '#c98a1b' }
const PAPER = { className: 'bg-board-paper text-ink-800', dot: '#7b8494' }

const STATUS_TINT: Record<string, { className: string; dot: string }> = {
  LEAD: PAPER,
  QUOTING: QUOTE,
  QUOTED: QUOTE,
  APPROVED: ORDER,
  SCHEDULED: ORDER,
  IN_PROGRESS: ORDER,
  ON_HOLD: PAPER,
  AWAITING_PARTS: QUOTE,
  COMPLETED: { className: 'bg-board-done text-board-done-ink', dot: '#3c8a5a' },
  CANCELLED: { className: 'bg-board-off text-ink-500', dot: '#a3a9b4' },
}

/** Ink over a pale fill — every status tint, and a pastel category. */
const ON_LIGHT = { muted: 'text-ink-600', subtle: 'text-ink-500' } as const
/** Ink over a fill dark enough that ink grey disappears into it. */
const ON_DARK = { muted: 'text-white/75', subtle: 'text-white/60' } as const

/**
 * Whether a fill needs white text over it.
 *
 * sRGB-weighted brightness, which is enough to choose between two inks and
 * cheaper than a contrast ratio. The threshold sits high on purpose: yellow and
 * lime are bright enough to need dark text despite being fully saturated.
 * Anything unparseable is treated as pale, because dark text on an unknown
 * colour is the safer of the two mistakes.
 */
export function isDarkFill(hex: string): boolean {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!match) return false

  const value = Number.parseInt(match[1]!, 16)
  const r = (value >> 16) & 0xff
  const g = (value >> 8) & 0xff
  const b = value & 0xff

  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.6
}

/**
 * A job's colour on the board.
 *
 * Takes the job rather than the visit, because a parked chip has no visit and
 * still has to wear the same colour as the booking it will become.
 *
 * **A category fill is the category's colour, opaque.** It used to be washed to
 * 15% so that mid-tone swatches would not shout, which quietly made the feature
 * useless against real data: almost every imported ServiceM8 category is a
 * pastel — `#b5d1ff`, `#ffb5b5`, `#feffcc` — and a pastel at 15% over white is
 * white. Colour-by-category drew a board of blank cards with the grid showing
 * through them. Opaque means the block is the colour the category says it is,
 * whatever that colour is, so the ink is chosen from the fill instead.
 *
 * An area fill is the same: the colour of the service area the job's suburb
 * is in, which the caller looks up (`useAreaOf`) because the areas are not on
 * the job. A job in no area, like a job with no category, keeps its status.
 */
export function blockTint(
  job: { status: string; category?: { colour: string } | null },
  colourBy: ColourBy,
  areaColour?: string | null,
): BlockTint {
  const colour =
    colourBy === 'category' ? job.category?.colour : colourBy === 'area' ? areaColour : null
  if (colour) {
    const dark = isDarkFill(colour)
    return {
      className: dark ? 'text-white' : 'text-ink-900',
      style: { backgroundColor: colour },
      ...(dark ? ON_DARK : ON_LIGHT),
      dot: colour,
    }
  }

  const status = STATUS_TINT[job.status] ?? STATUS_TINT.LEAD!
  return { className: status.className, dot: status.dot, ...ON_LIGHT }
}

/**
 * `colour` mixed onto white until its deepest channel is `depth` of the way
 * from white — about a Tailwind 200 tint for a mid tone.
 *
 * Opaque on purpose: an alpha fill lets the grid lines, a shaded row and any
 * hatch underneath show through the card, and the block reads as a ghost.
 *
 * A fixed percentage is not enough, because the imported ServiceM8 colours are
 * pastels already and a 15% wash of one is white. Mixing to one depth keeps a
 * pastel as itself and brings a saturated swatch down to the same weight, so
 * every category reads equally strongly behind dark text.
 */
export function washOnWhite(colour: string, depth = 0.25): string {
  const hex = colour.trim().replace(/^#/, '')
  const full =
    hex.length === 3 || hex.length === 4
      ? [...hex.slice(0, 3)].map((digit) => digit + digit).join('')
      : hex.slice(0, 6)
  if (!/^[0-9a-f]{6}$/i.test(full)) {
    return `color-mix(in srgb, ${colour} 30%, white)`
  }
  const channels = [0, 2, 4].map((at) => Number.parseInt(full.slice(at, at + 2), 16))
  const distance = (255 - Math.min(...channels)) / 255
  const amount = distance === 0 ? 0 : Math.min(1, depth / distance)
  const mixed = channels.map((channel) =>
    Math.round(255 - (255 - channel) * amount)
      .toString(16)
      .padStart(2, '0'),
  )
  return `#${mixed.join('')}`
}

/**
 * The day in figures.
 *
 * Plain numbers rather than tiles, because they are read on one line in the
 * date header now. They used to be a row of cards above the grid and that row
 * cost about eighty pixels of the board — the thing the numbers describe.
 */
export interface BoardPulse {
  /** Live bookings on the board. A revisit is two of these and one job. */
  bookedVisits: number
  /** Distinct jobs behind those bookings. */
  jobsOnBoard: number
  /** Live bookings already past where they should be. */
  runningLate: number
  drivingMinutes: number
  longestRunMinutes: number
}

export function boardPulse(
  visits: BoardVisit[],
  visitsByPerson: Map<string, BoardVisit[]>,
  now = new Date(),
): BoardPulse {
  const live = visits.filter(isLiveVisit)
  const jobIds = new Set(live.map((visit) => visit.job.id))
  const late = live.filter((visit) => isRunningLate(visit, now)).length
  const drivingMinutes = [...visitsByPerson.values()].reduce(
    (sum, rows) => sum + totalDriveMinutes(rows),
    0,
  )

  let longestRunMinutes = 0
  for (const rows of visitsByPerson.values()) {
    const booked = loadOf(rows)
    if (booked > longestRunMinutes) longestRunMinutes = booked
  }

  return {
    bookedVisits: live.length,
    jobsOnBoard: jobIds.size,
    runningLate: late,
    drivingMinutes,
    longestRunMinutes,
  }
}

export function staffFlag(person: SchedulablePerson): string | null {
  const role = person.role?.name ?? ''
  if (/apprentice/i.test(role)) return 'Apprentice'
  return null
}
